import { apiClient } from '@/services/apiClient';

/** The Agile Plan's running Notes card, stored on the Planner (O49). */
export interface RunningNote {
  note: string;
  updated_at?: string | null;
  /** false: this client has no Planner client yet, so there is nothing to edit. */
  available?: boolean;
  /** true: the backend has a Drive folder configured, so files can be attached. */
  attachments_enabled?: boolean;
}

/** What the backend answers for an uploaded attachment (a Google Drive file). */
export interface NoteFile {
  name: string;
  url: string;
  id: string;
  size: number | null;
  mime_type?: string;
  created_time?: string;
  modified_time?: string;
  icon_url?: string;
}

/** The files in the client's Drive folder (newest first) and a link to the folder. */
export interface NoteFileList {
  files: NoteFile[];
  folder_url?: string | null;
}

/** Longest note the backend accepts (characters). */
export const RUNNING_NOTE_MAX_CHARS = 10_000;

export const plannerNotesService = {
  async get(tenantId: string): Promise<RunningNote> {
    const { data } = await apiClient.get<RunningNote>('/planner/notes', {
      params: { tenant_id: tenantId },
    });
    return data;
  },

  async save(tenantId: string, note: string): Promise<RunningNote> {
    const { data } = await apiClient.put<RunningNote>(
      '/planner/notes',
      { note },
      { params: { tenant_id: tenantId } },
    );
    return data;
  },

  /** Upload one attachment; it lands in the client's Google Drive folder. */
  async uploadFile(
    tenantId: string, file: File, onProgress?: (percent: number) => void,
  ): Promise<NoteFile> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post<NoteFile>('/planner/notes/files', form, {
      params: { tenant_id: tenantId },
      // Without this the client's JSON default is sent and the server finds no `file` (422).
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e: { loaded?: number; total?: number; progress?: number }) => {
        const fraction = e.total ? (e.loaded ?? 0) / e.total : e.progress;
        if (onProgress && fraction !== undefined) {
          onProgress(Math.max(0, Math.min(100, Math.round(fraction * 100))));
        }
      },
    });
    return data;
  },

  async listFiles(tenantId: string): Promise<NoteFileList> {
    const { data } = await apiClient.get<NoteFileList>('/planner/notes/files', {
      params: { tenant_id: tenantId },
    });
    return { files: data?.files ?? [], folder_url: data?.folder_url ?? null };
  },

  /** Moves the file to the Drive trash (recoverable there). */
  async deleteFile(tenantId: string, fileId: string): Promise<void> {
    await apiClient.delete(`/planner/notes/files/${encodeURIComponent(fileId)}`, {
      params: { tenant_id: tenantId },
    });
  },
};
